import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Users, Search, ChevronLeft, Loader2, Shield, 
  Briefcase, User, MoreHorizontal, Check, X, Trash2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminUsers() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [total, setTotal] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchUsers();
  }, [user, search, roleFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (roleFilter !== 'all') params.set('role', roleFilter);
      
      const response = await fetch(`${API_URL}/api/admin/users?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
        setTotal(data.total);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ role: newRole }),
      });
      if (response.ok) {
        toast.success('Rôle mis à jour');
        fetchUsers();
        setDialogOpen(false);
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const toggleUserStatus = async (userId, isActive) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ is_active: isActive }),
      });
      if (response.ok) {
        toast.success(isActive ? 'Utilisateur activé' : 'Utilisateur désactivé');
        fetchUsers();
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const deleteUser = async (userId, userName) => {
    if (!window.confirm(`Supprimer ${userName} ? Cette action est irréversible.`)) return;
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token || localStorage.getItem('token')}` },
      });
      if (response.ok) {
        toast.success('Utilisateur supprimé');
        fetchUsers();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin':
        return <Badge className="bg-red-500">Admin</Badge>;
      case 'provider':
        return <Badge className="bg-accent">Prestataire</Badge>;
      default:
        return <Badge variant="secondary">Client</Badge>;
    }
  };

  return (
    <div className="min-h-screen bg-background pt-24 pb-16" data-testid="admin-users">
      <div className="container-app">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" onClick={() => navigate('/admin')}>
            <ChevronLeft className="w-4 h-4 mr-1" />
            Retour
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-semibold">Gestion des utilisateurs</h1>
            <p className="text-muted-foreground">{total} utilisateurs</p>
          </div>
        </div>

        {/* Filters */}
        <Card className="dashboard-card mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  placeholder="Rechercher par nom ou email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filtrer par rôle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les rôles</SelectItem>
                  <SelectItem value="client">Clients</SelectItem>
                  <SelectItem value="provider">Prestataires</SelectItem>
                  <SelectItem value="admin">Admins</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Users List */}
        <Card className="dashboard-card">
          <CardContent className="pt-6">
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-accent" />
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground">Aucun utilisateur trouvé</p>
              </div>
            ) : (
              <div className="space-y-3">
                {users.map((u) => (
                  <div
                    key={u.user_id}
                    className="flex items-center justify-between p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                        {u.role === 'admin' ? (
                          <Shield className="w-5 h-5 text-red-500" />
                        ) : u.role === 'provider' ? (
                          <Briefcase className="w-5 h-5 text-accent" />
                        ) : (
                          <User className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium">{u.name}</p>
                        <p className="text-sm text-muted-foreground">{u.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {getRoleBadge(u.role)}
                      {u.is_active === false && (
                        <Badge variant="destructive">Désactivé</Badge>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => {
                            setSelectedUser(u);
                            setDialogOpen(true);
                          }}>
                            Changer le rôle
                          </DropdownMenuItem>
                          {u.is_active !== false ? (
                            <DropdownMenuItem 
                              className="text-destructive"
                              onClick={() => toggleUserStatus(u.user_id, false)}
                            >
                              Désactiver
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem 
                              className="text-success"
                              onClick={() => toggleUserStatus(u.user_id, true)}
                            >
                              Activer
                            </DropdownMenuItem>
                          )}
                          {u.role !== 'admin' && (
                            <DropdownMenuItem 
                              className="text-destructive"
                              onClick={() => deleteUser(u.user_id, u.name)}
                              data-testid={`delete-user-${u.user_id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Supprimer
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Change Role Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Changer le rôle de {selectedUser?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-4">
              <Button
                variant={selectedUser?.role === 'client' ? 'default' : 'outline'}
                className="w-full justify-start"
                onClick={() => updateUserRole(selectedUser?.user_id, 'client')}
              >
                <User className="w-4 h-4 mr-2" />
                Client
                {selectedUser?.role === 'client' && <Check className="w-4 h-4 ml-auto" />}
              </Button>
              <Button
                variant={selectedUser?.role === 'provider' ? 'default' : 'outline'}
                className="w-full justify-start"
                onClick={() => updateUserRole(selectedUser?.user_id, 'provider')}
              >
                <Briefcase className="w-4 h-4 mr-2" />
                Prestataire
                {selectedUser?.role === 'provider' && <Check className="w-4 h-4 ml-auto" />}
              </Button>
              <Button
                variant={selectedUser?.role === 'admin' ? 'default' : 'outline'}
                className="w-full justify-start"
                onClick={() => updateUserRole(selectedUser?.user_id, 'admin')}
              >
                <Shield className="w-4 h-4 mr-2" />
                Administrateur
                {selectedUser?.role === 'admin' && <Check className="w-4 h-4 ml-auto" />}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
